/**
 * リロードをまたいで会話を復元するための localStorage 永続化。
 *
 * session_token と表示中メッセージを **delivery 単位**で保存し、再マウント時に
 * 復元して `/chat/start`（＝新規セッション生成）を回避する。既存 widget と同じ
 * クライアント保持方式で、サーバー変更を伴わない。
 *
 * - 保存単位が delivery なのは、同一ブラウザで別 delivery を開いても混ざらないため。
 * - TTL はサーバーの session_token 有効期限（SESSION_TTL_SECONDS = 24h）に合わせる。
 *   有効期限は**セッション生成時からの絶対時刻** `expiresAt` で持ち、以降の保存では
 *   上書きしない（毎回の保存で延命するとサーバー token の実期限から乖離するため）。
 *   期限内でもサーバー側セッションが無効なら送信時に 401(invalid_session) が返るので、
 *   呼び出し側がそこで clear + 再初期化してフォールバックする。
 * - localStorage 不可（シークレットモード等）や壊れた値は握りつぶして未復元扱いにする。
 */
import type { ChatMessage } from './types';

const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

// ChatMessage / MessageBlock の形を変えたらこの値を上げる。旧バージョンの保存は
// loadSession で破棄され、レンダラに旧形式が渡ってホワイトスクリーンになるのを防ぐ。
const SESSION_SCHEMA_VERSION = 1;

interface StoredSession {
  schemaVersion: number;
  token: string;
  messages: ChatMessage[];
  expiresAt: number;
}

function storageKey(deliveryId: string): string {
  return `suzanoh.session.${deliveryId}`;
}

export function loadSession(
  deliveryId: string,
): { token: string; messages: ChatMessage[] } | null {
  try {
    const raw = window.localStorage.getItem(storageKey(deliveryId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (
      !parsed ||
      parsed.schemaVersion !== SESSION_SCHEMA_VERSION ||
      typeof parsed.token !== 'string' ||
      !Array.isArray(parsed.messages) ||
      typeof parsed.expiresAt !== 'number'
    ) {
      clearSession(deliveryId);
      return null;
    }
    if (Date.now() >= parsed.expiresAt) {
      clearSession(deliveryId);
      return null;
    }
    return { token: parsed.token, messages: parsed.messages };
  } catch {
    return null;
  }
}

export function saveSession(
  deliveryId: string,
  token: string,
  messages: ChatMessage[],
): void {
  try {
    const key = storageKey(deliveryId);
    // expiresAt はセッション生成時に一度だけ確定させ、以降の保存では既存値を引き継ぐ。
    let expiresAt: number | undefined;
    const existing = window.localStorage.getItem(key);
    if (existing) {
      try {
        const prev = JSON.parse(existing) as StoredSession;
        if (typeof prev.expiresAt === 'number') expiresAt = prev.expiresAt;
      } catch {
        /* 壊れた既存値は無視して張り直す */
      }
    }
    if (typeof expiresAt !== 'number') expiresAt = Date.now() + SESSION_TTL_MS;
    const payload: StoredSession = {
      schemaVersion: SESSION_SCHEMA_VERSION,
      token,
      messages,
      expiresAt,
    };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    /* localStorage 不可時はメモリのみで継続（復元はできないが動作はする） */
  }
}

export function clearSession(deliveryId: string): void {
  try {
    window.localStorage.removeItem(storageKey(deliveryId));
  } catch {
    /* ignore */
  }
}
